import { makeAutoObservable, runInAction } from "mobx";
import type { FileSystemDirectoryHandle, FileSystemFileHandle } from '../types/filesystem';
import { readID3TagsFromHandle } from '../utilities/id3Reader';
import { scanDirectoryForAlbumArt, associateAlbumArtWithMusicFiles } from '../utilities/albumArtDetector';
import { storageService, type SerializableMusicFile, type LibraryMetadata, type AlbumArtFile } from '../services/storageService';

// Supported audio file extensions
const SUPPORTED_AUDIO_EXTENSIONS = [
    '.mp3', '.flac', '.wav', '.m4a', '.aac', '.ogg', '.wma'
];

const BASE64_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const BASE64_LOOKUP = new Uint8Array(256);
BASE64_LOOKUP.fill(255);
for (let i = 0; i < BASE64_CHARS.length; i++) {
    BASE64_LOOKUP[BASE64_CHARS.charCodeAt(i)] = i;
}
BASE64_LOOKUP['='.charCodeAt(0)] = 0;

function arrayBufferToBase64(buffer: ArrayBuffer): string {
    return uint8ToBase64(new Uint8Array(buffer));
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
    return base64ToUint8(base64).buffer as ArrayBuffer;
}

function uint8ToBase64(bytes: Uint8Array): string {
    let output = '';
    let i = 0;

    for (; i + 2 < bytes.length; i += 3) {
        const triplet = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
        output += BASE64_CHARS.charAt((triplet >> 18) & 0x3F);
        output += BASE64_CHARS.charAt((triplet >> 12) & 0x3F);
        output += BASE64_CHARS.charAt((triplet >> 6) & 0x3F);
        output += BASE64_CHARS.charAt(triplet & 0x3F);
    }

    const remaining = bytes.length - i;
    if (remaining === 1) {
        const byte = bytes[i];
        output += BASE64_CHARS.charAt((byte >> 2) & 0x3F);
        output += BASE64_CHARS.charAt((byte & 0x03) << 4);
        output += '==';
    } else if (remaining === 2) {
        const triplet = (bytes[i] << 8) | bytes[i + 1];
        output += BASE64_CHARS.charAt((triplet >> 10) & 0x3F);
        output += BASE64_CHARS.charAt((triplet >> 4) & 0x3F);
        output += BASE64_CHARS.charAt((triplet & 0x0F) << 2);
        output += '=';
    }

    return output;
}

function base64ToUint8(base64: string): Uint8Array {
    const sanitized = base64.replace(/\s/g, '');
    if (sanitized.length % 4 !== 0) {
        throw new Error('Invalid base64 string length');
    }

    const padding = sanitized.endsWith('==') ? 2 : sanitized.endsWith('=') ? 1 : 0;
    const outputLength = (sanitized.length / 4) * 3 - padding;
    const bytes = new Uint8Array(outputLength);

    let byteIndex = 0;
    for (let i = 0; i < sanitized.length; i += 4) {
        const enc1 = BASE64_LOOKUP[sanitized.charCodeAt(i)];
        const enc2 = BASE64_LOOKUP[sanitized.charCodeAt(i + 1)];
        const enc3 = BASE64_LOOKUP[sanitized.charCodeAt(i + 2)];
        const enc4 = BASE64_LOOKUP[sanitized.charCodeAt(i + 3)];

        if (enc1 === 255 || enc2 === 255 || enc3 === 255 || enc4 === 255) {
            throw new Error('Invalid character in base64 string');
        }

        const combined = (enc1 << 18) | (enc2 << 12) | (enc3 << 6) | enc4;

        if (byteIndex < outputLength) {
            bytes[byteIndex++] = (combined >> 16) & 0xFF;
        }
        if (byteIndex < outputLength) {
            bytes[byteIndex++] = (combined >> 8) & 0xFF;
        }
        if (byteIndex < outputLength) {
            bytes[byteIndex++] = combined & 0xFF;
        }
    }

    return bytes;
}

export interface Album {
    artist: Artist;
    title: string;
    tracks: MusicFile[];
}

export interface Artist {
    name: string;
    albums: Album[];
}

export interface Collection {
    artists: Artist[];
    albums: Album[];
}

export interface MusicFile {
    name: string;
    path: string;
    size: number;
    lastModified: number;
    fileHandle: FileSystemFileHandle;
    // Audio metadata (can be extended later)
    duration?: number;
    artist?: string;
    album?: string;
    title?: string;
    year?: string;
    trackNumber?: number;
    albumArt?: {
        data: ArrayBuffer;
        mimeType: string;
        description?: string;
        pictureType?: number;
    };
    // File-based album art (discovered in directory)
    albumArtFile?: {
        id: string;
        filename: string;
        mimeType: string;
    };
}

class LibraryStore {
    //@observable
    public rootDirectory: string | null = null;
    public rootDirectoryHandle: FileSystemDirectoryHandle | null = null;
    public musicFiles: MusicFile[] = [];
    public isScanning = false;
    public scanProgress = 0;
    public totalFilesFound = 0;
    public isLoading = false;
    public lastSaveDate: number | null = null;

    constructor() {
        makeAutoObservable(this);
        this.initializeStorage();
    }

    /**
     * Open directory picker and scan for music files
     */
    async selectMusicDirectory(): Promise<void> {
        try {
            // Check if File System API is supported
            if (!('showDirectoryPicker' in window)) {
                throw new Error('File System API is not supported in this browser');
            }

            // Type assertion for the showDirectoryPicker method
            const showDirectoryPicker = (window as any).showDirectoryPicker as (options?: {
                mode?: 'read' | 'readwrite';
                startIn?: string;
            }) => Promise<FileSystemDirectoryHandle>;

            // Open directory picker
            const directoryHandle = await showDirectoryPicker({
                mode: 'read',
                startIn: 'music'
            });

            runInAction(() => {
                this.rootDirectoryHandle = directoryHandle;
                this.rootDirectory = directoryHandle.name;
            });

            // Start scanning for music files
            await this.scanMusicFiles(directoryHandle);

            // Save the library after successful scan
            await this.saveLibraryToStorage();

        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                // User cancelled the picker
                // console.log('Directory selection cancelled');
                return;
            }
            console.error('Error selecting music directory:', error);
            throw error;
        }
    }

    /**
     * Recursively scan directory for music files
     */
    private async scanMusicFiles(directoryHandle: FileSystemDirectoryHandle, currentPath = ''): Promise<void> {
        runInAction(() => {
            this.isScanning = true;
        });

        try {
            // First, scan for album art files in this directory and subdirectories
            console.log('Scanning for album art files...');
            const albumArtFiles = await scanDirectoryForAlbumArt(directoryHandle, currentPath);
            console.log(`Found ${albumArtFiles.length} album art files`);

            // Then scan for music files
            await this.scanMusicFilesRecursive(directoryHandle, currentPath, albumArtFiles);
        } finally {
            runInAction(() => {
                this.isScanning = false;
            });
        }
    }

    /**
     * Recursively scan directory for music files and associate with album art
     */
    private async scanMusicFilesRecursive(
        directoryHandle: FileSystemDirectoryHandle,
        currentPath = '',
        albumArtFiles: AlbumArtFile[] = []
    ): Promise<void> {
        for await (const [name, handle] of directoryHandle.entries()) {
            const fullPath = currentPath ? `${currentPath}/${name}` : name;

            if (handle.kind === 'directory') {
                // Recursively scan subdirectories
                await this.scanMusicFilesRecursive(handle as FileSystemDirectoryHandle, fullPath, albumArtFiles);
            } else if (handle.kind === 'file') {
                // Check if it's a supported audio file
                const extension = name.toLowerCase().substring(name.lastIndexOf('.'));
                if (SUPPORTED_AUDIO_EXTENSIONS.includes(extension)) {
                    const fileHandle = handle as FileSystemFileHandle;
                    const file = await fileHandle.getFile();

                    // Create base music file object
                    const musicFile: MusicFile = {
                        name: file.name,
                        path: fullPath,
                        size: file.size,
                        lastModified: file.lastModified,
                        fileHandle: fileHandle
                    };

                    // Read ID3 tags for MP3 files
                    if (extension === '.mp3') {
                        try {
                            const id3Tags = await readID3TagsFromHandle(fileHandle);
                            if (id3Tags.title) musicFile.title = id3Tags.title;
                            if (id3Tags.artist) musicFile.artist = id3Tags.artist;
                            if (id3Tags.album) musicFile.album = id3Tags.album;
                            if (id3Tags.year) musicFile.year = id3Tags.year;
                            if (id3Tags.trackNumber !== undefined) musicFile.trackNumber = id3Tags.trackNumber;
                            if (id3Tags.albumArt) musicFile.albumArt = id3Tags.albumArt;
                        } catch (error) {
                            console.warn(`Failed to read ID3 tags for ${file.name}:`, error);
                        }
                    }

                    // Associate with file-based album art
                    const musicPaths = [fullPath];
                    const albumArtAssociations = associateAlbumArtWithMusicFiles(musicPaths, albumArtFiles);
                    const associatedAlbumArt = albumArtAssociations.get(fullPath);

                    if (associatedAlbumArt) {
                        musicFile.albumArtFile = {
                            id: associatedAlbumArt.id,
                            filename: associatedAlbumArt.filename,
                            mimeType: associatedAlbumArt.mimeType
                        };
                    }

                    runInAction(() => {
                        this.musicFiles.push(musicFile);
                        this.totalFilesFound = this.musicFiles.length;
                    });
                }
            }
        }
    }

    /**
     * Clear the current library
     */
    clearLibrary(): void {
        runInAction(() => {
            this.rootDirectory = null;
            this.rootDirectoryHandle = null;
            this.musicFiles = [];
            this.totalFilesFound = 0;
            this.scanProgress = 0;
            this.lastSaveDate = null;
        });

        // Also clear stored data
        this.clearStoredLibrary().catch(error => {
            console.warn('Failed to clear stored library:', error);
        });
    }

    /**
     * Initialize storage service
     */
    private async initializeStorage(): Promise<void> {
        try {
            // console.log('Initializing storage...');
            await storageService.init();
            // console.log('Storage initialized, attempting to load library...');
            await this.loadLibraryFromStorage();
            // console.log(`Library loading completed`);
        } catch (error) {
            console.warn('Failed to initialize storage:', error);
        }
    }

    /**
     * Convert MusicFile to serializable format
     */
    private musicFileToSerializable(musicFile: MusicFile): SerializableMusicFile {
        // Create a clean serializable object, explicitly copying only the properties we need
        const serializable: SerializableMusicFile = {
            name: musicFile.name,
            path: musicFile.path,
            size: musicFile.size,
            lastModified: musicFile.lastModified
        };

        // Only add optional properties if they exist
        if (musicFile.duration !== undefined) {
            serializable.duration = musicFile.duration;
        }
        if (musicFile.artist) {
            serializable.artist = musicFile.artist;
        }
        if (musicFile.album) {
            serializable.album = musicFile.album;
        }
        if (musicFile.title) {
            serializable.title = musicFile.title;
        }
        if (musicFile.year) {
            serializable.year = musicFile.year;
        }
        if (musicFile.trackNumber !== undefined) {
            serializable.trackNumber = musicFile.trackNumber;
        }

        // Handle ID3 album art carefully - convert to base64 for storage
        if (musicFile.albumArt) {
            try {
                const base64String = arrayBufferToBase64(musicFile.albumArt.data);

                serializable.albumArt = {
                    data: base64String,
                    mimeType: musicFile.albumArt.mimeType,
                    description: musicFile.albumArt.description,
                    pictureType: musicFile.albumArt.pictureType
                };
            } catch (error) {
                console.warn('Error serializing album art for', musicFile.path, error);
                // Skip album art if there's an issue
            }
        }

        // Handle file-based album art reference
        if (musicFile.albumArtFile) {
            serializable.albumArtFile = {
                id: musicFile.albumArtFile.id,
                filename: musicFile.albumArtFile.filename,
                mimeType: musicFile.albumArtFile.mimeType
            };
        }

        return serializable;
    }

    /**
     * Convert serializable format back to MusicFile with file handle
     */
    private async serializableToMusicFile(
        serializable: SerializableMusicFile,
        fileHandles: Map<string, FileSystemFileHandle>
    ): Promise<MusicFile | null> {
        const fileHandle = fileHandles.get(serializable.path);
        if (!fileHandle) {
            console.warn(`File handle not found for path: ${serializable.path}`);
            return null;
        }

        const musicFile: MusicFile = {
            name: serializable.name,
            path: serializable.path,
            size: serializable.size,
            lastModified: serializable.lastModified,
            fileHandle,
            duration: serializable.duration,
            artist: serializable.artist,
            album: serializable.album,
            title: serializable.title,
            year: serializable.year,
            trackNumber: serializable.trackNumber
        };

        // Fix ID3 album art if it exists - convert base64 back to ArrayBuffer
        if (serializable.albumArt && serializable.albumArt.data) {
            try {
                const arrayBuffer = base64ToArrayBuffer(serializable.albumArt.data);

                musicFile.albumArt = {
                    data: arrayBuffer,
                    mimeType: serializable.albumArt.mimeType,
                    description: serializable.albumArt.description,
                    pictureType: serializable.albumArt.pictureType
                };
            } catch (error) {
                console.warn(`Failed to restore ID3 album art for ${serializable.path}:`, error);
                try {
                    const refreshedTags = await readID3TagsFromHandle(fileHandle);
                    if (refreshedTags.albumArt) {
                        musicFile.albumArt = refreshedTags.albumArt;
                        console.info(`Refreshed ID3 album art directly from file for ${serializable.path}`);
                    }
                } catch (refreshError) {
                    console.warn(`Unable to refresh ID3 album art for ${serializable.path}:`, refreshError);
                }
            }
        }

        // Restore file-based album art reference
        if (serializable.albumArtFile) {
            musicFile.albumArtFile = {
                id: serializable.albumArtFile.id,
                filename: serializable.albumArtFile.filename,
                mimeType: serializable.albumArtFile.mimeType
            };
        }

        return musicFile;
    }

    /**
     * Save current library state to IndexedDB
     */
    async saveLibraryToStorage(): Promise<void> {
        try {
            if (!this.rootDirectoryHandle || !this.rootDirectory) {
                console.warn('No library to save');
                return;
            }

            // console.log(`Starting to save library with ${this.musicFiles.length} files`);

            // Save library metadata
            const metadata: LibraryMetadata = {
                id: 'main',
                rootDirectory: this.rootDirectory,
                lastScanDate: Date.now(),
                totalFiles: this.musicFiles.length,
                version: 1
            };

            await storageService.saveLibraryMetadata(metadata);
            // console.log('Saved library metadata');

            // Save root directory handle
            await storageService.saveRootDirectoryHandle(this.rootDirectoryHandle);
            // console.log('Saved root directory handle');

            // Save all file handles
            for (const musicFile of this.musicFiles) {
                try {
                    await storageService.saveFileHandle(musicFile.path, musicFile.fileHandle);
                    // console.log(`Saved file handle for: ${musicFile.path}`);
                } catch (error) {
                    console.error(`Failed to save file handle for ${musicFile.path}:`, error);
                }
            }

            // Save music files data (without handles)
            const serializableFiles = this.musicFiles.map(file => this.musicFileToSerializable(file));
            // console.log(`About to save ${serializableFiles.length} serializable files`);
            try {
                await storageService.saveMusicFiles(serializableFiles);
                // console.log(`Saved ${serializableFiles.length} music files data`);
            } catch (error) {
                console.error('Error saving music files:', error);
                throw error;
            }

            runInAction(() => {
                this.lastSaveDate = Date.now();
            });
            // console.log(`Saved library with ${this.musicFiles.length} files to storage`);

        } catch (error) {
            console.error('Failed to save library to storage:', error);
            throw error;
        }
    }

    /**
     * Load library state from IndexedDB
     */
    async loadLibraryFromStorage(): Promise<boolean> {
        try {
            runInAction(() => {
                this.isLoading = true;
            });

            // Check if we have stored library data
            const hasStoredData = await storageService.hasStoredLibrary();
            if (!hasStoredData) {
                return false;
            }

            // Load metadata
            const metadata = await storageService.loadLibraryMetadata();
            if (!metadata) {
                return false;
            }

            // Load root directory handle
            const rootHandle = await storageService.loadRootDirectoryHandle();
            if (!rootHandle) {
                console.warn('Root directory handle not found in storage');
                return false;
            }

            // Verify permissions for root directory
            const hasPermission = await this.verifyDirectoryPermission(rootHandle);
            if (!hasPermission) {
                console.warn('Permission denied for stored root directory');
                return false;
            }

            // Load all file handles
            const allHandles = await storageService.loadAllFileHandles();
            const fileHandles = new Map<string, FileSystemFileHandle>();

            // Filter out non-file handles and verify they're still accessible
            for (const [path, handle] of allHandles) {
                if (handle.kind === 'file' && path !== 'ROOT_DIRECTORY') {
                    try {
                        // Verify the file handle is still valid
                        const fileHandle = handle as FileSystemFileHandle;
                        await fileHandle.getFile(); // This will throw if permission is denied or file is gone
                        fileHandles.set(path, fileHandle);
                    } catch (error) {
                        console.warn(`File handle no longer accessible: ${path}`, error);
                        // Continue without this file - it will be removed from the library
                    }
                }
            }

            // Load serializable music files
            const serializableFiles = await storageService.loadMusicFiles();

            // Convert back to MusicFile objects with valid handles
            const musicFiles: MusicFile[] = [];
            for (const serializable of serializableFiles) {
                const musicFile = await this.serializableToMusicFile(serializable, fileHandles);
                if (musicFile) {
                    musicFiles.push(musicFile);
                }
            }            // Update store state
            runInAction(() => {
                this.rootDirectoryHandle = rootHandle;
                this.rootDirectory = metadata.rootDirectory;
                this.musicFiles = musicFiles;
                this.totalFilesFound = musicFiles.length;
                this.lastSaveDate = metadata.lastScanDate;
            });

            // console.log(`Loaded library with ${musicFiles.length} files from storage`);

            // If we lost some files, save the updated state
            if (musicFiles.length < serializableFiles.length) {
                // console.log(`${serializableFiles.length - musicFiles.length} files were no longer accessible and removed`);
                await this.saveLibraryToStorage();
            }

            return true;

        } catch (error) {
            console.error('Failed to load library from storage:', error);
            return false;
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    /**
     * Verify permission for a directory handle
     */
    private async verifyDirectoryPermission(directoryHandle: FileSystemDirectoryHandle): Promise<boolean> {
        try {
            // Try to query permission first
            const permission = await directoryHandle.queryPermission({ mode: 'read' });

            if (permission === 'granted') {
                return true;
            }

            if (permission === 'prompt') {
                // Request permission
                const requestedPermission = await directoryHandle.requestPermission({ mode: 'read' });
                return requestedPermission === 'granted';
            }

            return false;
        } catch (error) {
            console.warn('Error verifying directory permission:', error);
            return false;
        }
    }

    /**
     * Rescan the current directory to verify files and find new ones
     */
    async rescanLibrary(): Promise<void> {
        if (!this.rootDirectoryHandle) {
            throw new Error('No root directory handle available for rescanning');
        }

        // Clear current files and rescan
        runInAction(() => {
            this.musicFiles = [];
            this.totalFilesFound = 0;
        });

        await this.scanMusicFiles(this.rootDirectoryHandle);

        // Save updated library
        await this.saveLibraryToStorage();

        console.log(`Rescan complete: found ${this.musicFiles.length} music files`);
    }

    /**
     * Clear stored library data
     */
    async clearStoredLibrary(): Promise<void> {
        try {
            await storageService.clearAll();
            runInAction(() => {
                this.lastSaveDate = null;
            });
            // console.log('Cleared stored library data');
        } catch (error) {
            console.error('Failed to clear stored library:', error);
            throw error;
        }
    }

    /**
     * Get music file by path
     */
    getMusicFile(path: string): MusicFile | undefined {
        return this.musicFiles.find(file => file.path === path);
    }

    get collection(): Collection {
        const artistsMap = new Map<string, Artist>();
        const albumsMap = new Map<string, Album>();

        this.musicFiles.forEach(file => {
            const artistName = file.artist || 'Unknown Artist';
            const albumTitle = file.album || 'Unknown Album';

            // Get or create artist
            let artist = artistsMap.get(artistName);
            if (!artist) {
                artist = { name: artistName, albums: [] };
                artistsMap.set(artistName, artist);
            }

            // Get or create album
            const albumKey = `${artistName}||${albumTitle}`;
            let album = albumsMap.get(albumKey);
            if (!album) {
                album = { artist, title: albumTitle, tracks: [] };
                albumsMap.set(albumKey, album);
                artist.albums.push(album);
            }

            // Add track to album, in order by track number
            //const track = file;
            album.tracks.push(file);
        });

        albumsMap.forEach(album => {
            album.tracks.sort((a, b) => {
                const aTrack = a.trackNumber;
                const bTrack = b.trackNumber;

                if (aTrack !== undefined && bTrack !== undefined && aTrack !== bTrack) {
                    return aTrack - bTrack;
                }

                if (aTrack !== undefined && bTrack === undefined) {
                    return -1;
                }

                if (aTrack === undefined && bTrack !== undefined) {
                    return 1;
                }

                const aName = a.title || a.name;
                const bName = b.title || b.name;
                return aName.localeCompare(bName);
            });
        });

        return {
            artists: Array.from(artistsMap.values()),
            albums: Array.from(albumsMap.values())
        };
    }

    getMusicFilesByArtist(artist: string): MusicFile[] {
        return this.musicFiles.filter(file => file.artist === artist);
    }

    getMusicFilesByAlbum(album: string): MusicFile[] {
        return this.musicFiles.filter(file => file.album === album);
    }

    /**
     * Get all music files
     */
    getAllMusicFiles(): MusicFile[] {
        return [...this.musicFiles];
    }

    /**
     * Get music files filtered by extension
     */
    getMusicFilesByExtension(extension: string): MusicFile[] {
        return this.musicFiles.filter(file =>
            file.name.toLowerCase().endsWith(extension.toLowerCase())
        );
    }

    /**
     * Create a blob URL for album art display
     * Prefers ID3 album art over file-based album art
     */
    getAlbumArtUrl(musicFile: MusicFile): string | null {
        // Prefer ID3 album art if available
        if (musicFile.albumArt) {
            try {
                const blob = new Blob([musicFile.albumArt.data], {
                    type: musicFile.albumArt.mimeType
                });
                return URL.createObjectURL(blob);
            } catch (error) {
                console.warn('Error creating ID3 album art URL for', musicFile.path, error);
            }
        }

        // Fall back to file-based album art
        if (musicFile.albumArtFile) {
            // Note: This returns null for now, actual loading would require async operation
            // Consider implementing a separate async method for file-based album art
            console.log(`File-based album art available for ${musicFile.path}: ${musicFile.albumArtFile.filename}`);
        }

        return null;
    }

    /**
     * Get album art URL from file-based album art (async version)
     */
    async getFileBasedAlbumArtUrl(musicFile: MusicFile): Promise<string | null> {
        if (!musicFile.albumArtFile) {
            return null;
        }

        try {
            const albumArt = await storageService.loadAlbumArt(musicFile.albumArtFile.id);
            if (albumArt) {
                const blob = new Blob([albumArt.data], { type: albumArt.mimeType });
                return URL.createObjectURL(blob);
            }
        } catch (error) {
            console.warn(`Failed to load file-based album art for ${musicFile.path}:`, error);
        }

        return null;
    }

    /**
     * Get the best available album art URL (async, checks both ID3 and file-based)
     */
    async getBestAlbumArtUrl(musicFile: MusicFile): Promise<string | null> {
        // Prefer ID3 album art
        const id3Url = this.getAlbumArtUrl(musicFile);
        if (id3Url) {
            return id3Url;
        }

        // Fall back to file-based album art
        return await this.getFileBasedAlbumArtUrl(musicFile);
    }

    /**
     * Clean up blob URLs when no longer needed
     */
    revokeAlbumArtUrl(url: string): void {
        try {
            URL.revokeObjectURL(url);
        } catch (error) {
            console.warn('Error revoking album art URL:', error);
        }
    }

    /**
     * Get unique albums with metadata, sorted by artist-album-year
     */
    getAlbumsGrouped(): Array<{
        artist: string;
        album: string;
        year: string;
        sortKey: string;
        files: MusicFile[];
        albumArt?: MusicFile['albumArt'];
        albumArtFile?: MusicFile['albumArtFile'];
    }> {
        const albumMap = new Map<string, {
            artist: string;
            album: string;
            year: string;
            sortKey: string;
            files: MusicFile[];
            albumArt?: MusicFile['albumArt'];
            albumArtFile?: MusicFile['albumArtFile'];
        }>();

        this.musicFiles.forEach(file => {
            const artist = file.artist || 'Unknown Artist';
            const album = file.album || 'Unknown Album';
            const year = file.year || '';
            const sortKey = `${artist}-${album}-${year}`;

            if (!albumMap.has(sortKey)) {
                albumMap.set(sortKey, {
                    artist,
                    album,
                    year,
                    sortKey,
                    files: [],
                    albumArt: undefined,
                    albumArtFile: undefined
                });
            }

            const albumData = albumMap.get(sortKey)!;
            albumData.files.push(file);

            // Use first file with ID3 album art found (preferred)
            if (!albumData.albumArt && file.albumArt) {
                albumData.albumArt = file.albumArt;
            }

            // Use first file with file-based album art found (fallback)
            if (!albumData.albumArtFile && file.albumArtFile) {
                albumData.albumArtFile = file.albumArtFile;
            }
        });

        // Convert to array and sort by sortKey
        return Array.from(albumMap.values()).sort((a, b) =>
            a.sortKey.localeCompare(b.sortKey)
        );
    }
}

export const libraryStore = new LibraryStore();